/**
 * Code-aware error resolver for the legacy `POST /api/admin/users/:id/role`
 * editor. Since v2.0 (ADR-0017 A2) the owner's organization follows the
 * role, so the API refuses the change with organization conflicts
 * (`organization-error.mapper.ts`): every code gets our own copy and the
 * server message is never shown (it can name entities and is English only).
 * Side-effect free — unit-tested in `change-role-error.test.ts`.
 */
import { errorCodeOf } from "@/shared/api/error-code";
import { ApiError } from "@/shared/api/http-client";

type Translate = (key: string, values?: Record<string, unknown>) => string;

const BASE = "users.changeRole.errors";
const FORBIDDEN_KEY = "users.errors.forbidden";
const GENERIC_KEY = "users.changeRole.error";

const ERROR_KEY_BY_CODE: Readonly<Record<string, string>> = {
  ORGANIZATION_KIND_MISMATCH: `${BASE}.organizationKindMismatch`,
  TEST_PARTITION_MISMATCH: `${BASE}.testPartitionMismatch`,
  OWNER_MEMBERSHIP_PROTECTED: `${BASE}.ownerMembershipProtected`,
  ORGANIZATION_NOT_FOUND: `${BASE}.organizationNotFound`,
  USER_NOT_FOUND: `${BASE}.userNotFound`,
  SELF_ROLE_CHANGE_FORBIDDEN: `${BASE}.selfRoleChange`,
};

/** Uncoded statuses the use case still answers with a bare NestJS message. */
const KEY_BY_STATUS: Readonly<Record<number, string>> = {
  404: `${BASE}.userNotFound`,
  409: `${BASE}.roleUnchanged`,
  429: `${BASE}.throttled`,
};

interface NotEmptyCounts {
  readonly venues: number;
  readonly properties: number;
  readonly otherMembers: number;
}

const EMPTY_COUNTS: NotEmptyCounts = { venues: 0, properties: 0, otherMembers: 0 };

function countOf(details: Record<string, unknown>, field: keyof NotEmptyCounts): number {
  const value = details[field];
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : 0;
}

/** `ORGANIZATION_NOT_EMPTY` carries what blocks the change in `details`. */
function notEmptyCounts(err: ApiError): NotEmptyCounts {
  const details = (err.body as { details?: unknown }).details;
  if (!details || typeof details !== "object") return EMPTY_COUNTS;
  const record = details as Record<string, unknown>;
  return {
    venues: countOf(record, "venues"),
    properties: countOf(record, "properties"),
    otherMembers: countOf(record, "otherMembers"),
  };
}

export function resolveChangeRoleError(err: unknown, t: Translate): string {
  if (!(err instanceof ApiError)) return t(GENERIC_KEY);
  if (err.status === 401 || err.status === 403) {
    const code = errorCodeOf(err);
    return code === "SELF_ROLE_CHANGE_FORBIDDEN" ? t(ERROR_KEY_BY_CODE[code]) : t(FORBIDDEN_KEY);
  }
  const code = errorCodeOf(err);
  if (code === "ORGANIZATION_NOT_EMPTY") {
    return t(`${BASE}.organizationNotEmpty`, { ...notEmptyCounts(err) });
  }
  const byCode = code ? ERROR_KEY_BY_CODE[code] : undefined;
  if (byCode) return t(byCode);
  // A code we do not know on a status we do: the status still tells the story.
  const byStatus = code ? undefined : KEY_BY_STATUS[err.status];
  return t(byStatus ?? GENERIC_KEY);
}
